import React, { useState, useEffect } from 'react'
import { isInternationalOnshoreStudent } from '../../helpers/form'
import HelpText from '../tags/HelpText'
import { ExpandableRadioGroup, ExpandableRadioButton } from '../tags/ExpandableRadioGroup'
import { Refresh } from '@mui/icons-material'
import { FormControlLabel, Radio, RadioGroup, TextField } from '@mui/material'
import * as Yup from 'yup';
import { hasCharCountEqualTo, hasCharCountLessThanOrEqualTo, isNotEmpty, isValidUSI, requires, validateCheckCharacter } from '../../helpers/validation'

const dateFormat = { year: 'numeric', month: 'short', day: 'numeric' }

const isFirstFamilyNameIndicatorY = (value) => value === 'Y'
const isFirstFamilyNameIndicatorN = (value) => value === 'N'

const UsiDetailsForm = props => {
  const validationSchema = Yup.object().shape({
    legalFirstName: Yup.string()
        .when('firstFamilyNameIndicator', {
          is: (value) => isFirstFamilyNameIndicatorY(value),
          then: (schema) => {
            return schema.test(
              'isNotEmpty',
              'Please provide a First Name.',
              (value, context) => isNotEmpty(value)
            )
          }
        })
        .when('firstFamilyNameIndicator', {
          is: (value) => isFirstFamilyNameIndicatorY(value),
          then: (schema) => {
            return schema.test(
              'usi_char_count_lt_40',
              'First Name must be less than or equal to 40 characters long.',
              (value, context) => hasCharCountLessThanOrEqualTo(value, 40)
            )
          }
        }),
    firstFamilyNameIndicator: Yup.string().oneOf(['Y', 'N']),
    internationalStudentNotYetArrived: Yup.string().oneOf(['Y', 'N']),
    usiVerified: Yup.boolean(),
    singleName: Yup.string()
        .when('firstFamilyNameIndicator', {
          is: (value) => isFirstFamilyNameIndicatorN(value),
          then: (schema) => {
            return schema.test(
              'isNotEmpty',
              'Please provide a Single Name.',
              (value, context) => isNotEmpty(value)
            )
          }
        }),
    usiNo: Yup.string().nullable()
      .when(['usiVerified', 'internationalStudentNotYetArrived', 'stuCitizenCd', 'offshoreFlag'], {
        is: ((usiVerified, internationalStudentNotYetArrived, stuCitizenCd, offshoreFlag) => (isInternationalOnshoreStudent(stuCitizenCd, offshoreFlag) && !usiVerified && internationalStudentNotYetArrived === 'Y')) || !isInternationalOnshoreStudent(stuCitizenCd, offshoreFlag),
        then: (schema) => {
          return schema
            .required('Please provide Unique Student Identifier.')
            .test('charCount', 'Unique Student Identifier must be 10 characters long.', (value, context) => hasCharCountEqualTo(value, 10))
            .test('isValidUSI', 'Please input only A-H, J-N, P-Z, or 2-9.', (value, context) => isValidUSI(value))
            .test('validateCheckCharacter', 'Please enter a Valid USI.', (value, context) => validateCheckCharacter(value))
        }
      })
  });

  const [form, setForm] = useState({
    stuCitizenCd: '',
    offshoreFlag: '',
    dateOfBirth: '',
    singleName: '',
    firstName: '',
    familyName: '',
    usiNo: '',
    firstFamilyNameIndicator: 'N',
    usiVerified: false,
    internationalStudentNotYetArrived: 'N'
  })
  const [errors, setErrors] = useState({});

  useEffect(()=>{
    let internationalStudentNotYetArrived = props.values.internationalStudentNotYetArrived

    let stuCitizenCd = props.values.stuCitizenCd
    let offshoreFlag = props.values.offshoreFlag
    
    if (!isInternationalOnshoreStudent(stuCitizenCd, offshoreFlag)) {
      internationalStudentNotYetArrived = "Y"
    }

    setForm({...props.values, "internationalStudentNotYetArrived": internationalStudentNotYetArrived})
  },[])

  const handleChange = async (e) => {
    const { name, value } = e.target;
    
    setForm({...form, [name]: value});

    try {
      await validationSchema.validateAt(name, { ...form, [name]: value });
      setErrors({...errors, [name]: undefined});
    } catch (validationError) {
      setErrors({...errors,[name]: validationError.message});
    }
  };

  const handleChangeToUpperCase = async (e) => {
    const { name, value } = e.target;
    
    setForm({...form, [name]: value.toUpperCase()});

    try {
      await validationSchema.validateAt(name, { ...form, [name]: value });
      setErrors({...errors, [name]: undefined});
    } catch (validationError) {
      setErrors({...errors,[name]: validationError.message});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await validationSchema.validate(form, { abortEarly: false });
      setErrors({});
      if (isInternationalOnshoreStudent(form.stuCitizenCd, form.offshoreFlag) && !form.usiVerified && form.internationalStudentNotYetArrived === 'N') {
        const { progressForm: progressForm } = props
        progressForm()
      } else {
        props.handleSubmit({form: form, valid: true, errors: errors})
      }
    } catch (validationErrors) {
      const formattedErrors = validationErrors.inner.reduce((acc, error) => {
        return { ...acc, [error.path]: error.message };
      }, {});
      setErrors(formattedErrors);
    }
  };

  const handleProgress = event => {
    event.preventDefault()

    const { progressForm: progressForm } = props

    progressForm()
  }

  const getUSIForm = () => 
  (
    <div>
      <div className="formGroup question-group">
        <HelpText tag="H3" text={'Do you have a first name and family name?'} />

        <RadioGroup
          className="form-item"
          name={'firstFamilyNameIndicator'}
          onChange={handleChange}
          value={form.firstFamilyNameIndicator}
        >
          <FormControlLabel
            label="Yes"
            value={"Y"}
            control={<Radio />}
          />
          <FormControlLabel 
            label="No, I only have one name"
            value={"N"}
            control={<Radio />}
          />
        </RadioGroup>

        { form.firstFamilyNameIndicator === 'Y' ? 
        (
          <div className="row">
            <div className="col-xs-12 col-md-6">
              <h3>First Name</h3>
              <TextField 
                name='legalFirstName'
                fullWidth 
                value={form.legalFirstName} 
                variant='standard'
                disabled 
              />
            </div>

            <div className="col-xs-12 col-md-6">
              <h3>Family Name</h3>
              <TextField 
                name='legalLastName'
                fullWidth 
                value={form.legalLastName} 
                variant='standard'
                disabled 
              />
            </div>
          </div>
        )
        : (
          <div className="row">
            <div className="col-xs-12 col-md-6">
              <h3>Single Name</h3>
              <TextField
                name='singleName'
                error={!!errors.singleName}
                helperText={errors.singleName}
                fullWidth
                onChange={handleChange}
                value={form.singleName}
                variant='standard'
              />
            </div>
          </div>
        )
      }
      </div>

      <div className="question-group">
        <div className="row">
          <div className="col-xs-12 col-md-6">
            <h3>Date of Birth</h3>
              <TextField 
                fullWidth 
                value={new Date(form.dateOfBirth).toLocaleDateString('en-AU', { timeZone: 'Australia/Melbourne' }, dateFormat)} 
                disabled
                variant='standard'
              />
          </div>
        </div>
      </div>

      <div className="question-group">
        <div className="row">
          <div className="col-xs-12 col-md-6">
            <h3>Unique Student Identifier(USI)</h3>

            <TextField
              name="usiNo"
              error={
                props.showValidationMessage
                  ? true
                  : !!errors.usiNo 
              }
              helperText={
                props.showValidationMessage
                ? !!errors.usiNo
                  ? errors.usiNo
                  : 'We could not verify your USI - please try again, if error continues proceed to next step. We may contact you to obtain your verified USI.'
                : !!errors.usiNo
                  ? errors.usiNo
                  : false
              }
              onChange={handleChangeToUpperCase}
              label="Enter your 10 digit Unique Student Identifier (USI)"
              fullWidth
              value={form.usiNo || ''}
              variant='standard'
            />
          </div>
        </div>
      </div>

      {props.errorOnValidation && !props.saving ? (
        <div className="row invalid-usi-container">
          <div className="invalid-usi-btn-container">
            <button className="btn btn-lg">
              Retry <Refresh />
            </button>
            <button className="btn btn-lg btn-primary" onClick={this.handleProgress}>
              Continue
            </button>
          </div>
        </div>
      ) : null}

      <div className="row">
        <div className="col-xs-12 col-md-6">
          {props.saving ? (
            <button className="btn btn-lg btn-primary" disabled>
              Verifying...
            </button>
          ) : props.errorOnValidation ? null : (
            <button className="btn btn-lg btn-primary">Verify &amp; continue</button>
          )}
        </div>
      </div>
    </div>
  )

  return !form.usiVerified ? (
    isInternationalOnshoreStudent(form.stuCitizenCd, form.offshoreFlag) ? (
      <form onSubmit={handleSubmit}>
        <div className="formGroup question-group">
          <HelpText tag={'h3'} text={'Are you currently in Australia?'} />

          <ExpandableRadioGroup
            errorText={!!errors.internationalStudentNotYetArrived ? errors.internationalStudentNotYetArrived : false}
            name="internationalStudentNotYetArrived"
            onChange={handleChange}
            valueSelected={form.internationalStudentNotYetArrived}
          >
            <ExpandableRadioButton label="No" value="N" />
            <ExpandableRadioButton label="Yes" value="Y" />
          </ExpandableRadioGroup>
        </div>
        {form.internationalStudentNotYetArrived === 'Y' ? getUSIForm() : null}
        {form.internationalStudentNotYetArrived === 'N' ? <button className="btn btn-lg btn-primary">Continue</button> : null}
      </form>
    ) : (
      <form onSubmit={handleSubmit}>{getUSIForm()}</form>
    )
  ) : (
    <form onSubmit={handleProgress}>
      <div className="validated-usi-container">
        <p>
          <b>Your USI has been validated, please proceed to the next enrolment step.</b>
        </p>
        <h3>Unique Student Identifier(USI)</h3>
        <TextField 
          fullWidth 
          value={form.usiNo} 
          name="usiNo" 
          disabled
          variant='standard'
        />
        <button className="btn btn-lg btn-primary">Continue</button>
      </div>
    </form>
  )
}
export default UsiDetailsForm